import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import { useAppDispatch, useAppSelector } from "@store/hooks";
import {
  setWorkspaceData,
  setWorkspaceName,
} from "@store/slices/workspaceSlice";
import { selectSelectedOrgId } from "@store/slices/viewSlice";
import {
  deleteWorkspace,
  getWorkspaces,
  updateWorkspace,
} from "@services/api";
import type { RootState } from "@store/index";
import type { Workspace } from "@/types/models";

const WorkspaceSettingsScreen = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const workspaceId = useAppSelector(
    (state: RootState) => state.workspace.workspace_id
  );
  const currentName = useAppSelector(
    (state: RootState) => state.workspace.workspace_name
  );
  const selectedOrgId = useAppSelector(selectSelectedOrgId);

  const [name, setName] = useState(currentName);
  const [renaming, setRenaming] = useState(false);
  const [renameError, setRenameError] = useState<string>("");
  const [renameSuccess, setRenameSuccess] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string>("");

  // Sync local name field when the workspace context changes (e.g. user
  // picks another workspace from the switcher above the tabs).
  useEffect(() => {
    setName(currentName);
    setRenameSuccess(false);
    setRenameError("");
  }, [currentName, workspaceId]);

  const trimmedName = name.trim();
  const nameChanged = trimmedName !== currentName.trim();
  const nameValid = trimmedName.length > 0 && trimmedName.length <= 255;

  const handleRename = async () => {
    if (!nameValid || !nameChanged) return;
    setRenaming(true);
    setRenameError("");
    setRenameSuccess(false);
    const result = await updateWorkspace(workspaceId, {
      workspace_name: trimmedName,
    });
    setRenaming(false);
    if (!result.success) {
      setRenameError(result.error.message);
      return;
    }
    dispatch(setWorkspaceName(trimmedName));
    setRenameSuccess(true);
  };

  const openDelete = () => {
    setDeleteOpen(true);
    setConfirmText("");
    setDeleteError("");
  };
  const closeDelete = () => {
    if (deleting) return;
    setDeleteOpen(false);
    setConfirmText("");
    setDeleteError("");
  };

  const handleDelete = async () => {
    if (confirmText !== currentName) {
      setDeleteError("Type the workspace name exactly to confirm.");
      return;
    }
    setDeleting(true);
    setDeleteError("");

    const result = await deleteWorkspace(workspaceId);
    if (!result.success) {
      setDeleting(false);
      setDeleteError(result.error.message);
      return;
    }

    // Pick the next workspace to drop the user into. Prefer one in the
    // current context (Personal vs Org X), fall back to any. If none
    // exist, leave the workspace_id cleared and let /annogram render the
    // empty state.
    const wsResult = await getWorkspaces();
    let next: Workspace | null = null;
    if (wsResult.success && Array.isArray(wsResult.data)) {
      const remaining = wsResult.data.filter(
        (w) => w.workspace_id !== workspaceId
      );
      const inContext = remaining.filter((w) =>
        selectedOrgId === null ? !w.org_id : w.org_id === selectedOrgId
      );
      next = inContext[0] ?? remaining[0] ?? null;
    }

    if (next) {
      dispatch(
        setWorkspaceData({
          workspace_id: next.workspace_id,
          workspace_name: next.workspace_name,
          shared_data: next.shared_data,
          user_data: next.user_data,
          workspace_permissions: next.workspace_permissions,
        })
      );
    } else {
      // No remaining workspaces — clear context. The annotation view will
      // show its empty state; the user can create a new one from the
      // dropdown.
      dispatch(
        setWorkspaceData({
          workspace_id: "",
          workspace_name: "",
          shared_data: { categories: [] },
          user_data: { timeLog: 0 },
          workspace_permissions: [],
        })
      );
    }

    setDeleting(false);
    setDeleteOpen(false);
    navigate("/annogram");
  };

  if (!workspaceId) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="body2" color="text.secondary">
          No workspace selected.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, maxWidth: 720 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Identity
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The workspace name appears in the dropdown, the admin header, and
          anywhere this workspace is referenced.
        </Typography>
        {renameError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRenameError("")}>
            {renameError}
          </Alert>
        )}
        {renameSuccess && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setRenameSuccess(false)}
          >
            Workspace renamed.
          </Alert>
        )}
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
          <TextField
            label="Workspace name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            size="small"
            fullWidth
            inputProps={{ maxLength: 255 }}
            disabled={renaming}
            error={!nameValid}
            helperText={!nameValid ? "Required, 1-255 characters" : " "}
          />
          <Button
            variant="contained"
            onClick={handleRename}
            disabled={!nameChanged || !nameValid || renaming}
            startIcon={renaming ? <CircularProgress size={16} /> : null}
            sx={{ mt: 0.25 }}
          >
            {renaming ? "Saving…" : "Save"}
          </Button>
        </Box>
      </Paper>

      <Paper
        sx={{
          p: 3,
          borderColor: "error.main",
          borderWidth: 1,
          borderStyle: "solid",
        }}
      >
        <Typography variant="h6" color="error" gutterBottom>
          Danger zone
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Delete this workspace
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Permanently removes the workspace and every reading, canvas,
              paper, and member it contains. This cannot be undone.
            </Typography>
          </Box>
          <Button color="error" variant="outlined" onClick={openDelete}>
            Delete workspace
          </Button>
        </Box>
      </Paper>

      <Dialog open={deleteOpen} onClose={closeDelete} maxWidth="sm" fullWidth>
        <DialogTitle>Delete workspace</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This will permanently delete <strong>{currentName}</strong> along
            with its readings, canvases, papers, and member roster. Documents
            uploaded to this workspace will remain — readings reference them
            and would already be cascaded out, but the underlying PDFs stay
            in your library.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Type the workspace name to confirm:
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder={currentName}
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            disabled={deleting}
          />
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDelete} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleting || confirmText !== currentName}
            startIcon={deleting ? <CircularProgress size={16} /> : null}
          >
            {deleting ? "Deleting…" : "Delete workspace"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkspaceSettingsScreen;
